
import { useState, useRef, useEffect } from 'react';

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// Datas ocupadas (partilhadas com o AvailabilityBanner)
const BOOKED_RANGES: { start: string; end: string }[] = [
  { start: '2025-07-01', end: '2025-07-03' },
  { start: '2025-07-07', end: '2025-07-11' },
  { start: '2025-07-14', end: '2025-07-19' },
  { start: '2025-07-22', end: '2025-07-26' },
  { start: '2025-08-01', end: '2025-08-08' },
  { start: '2025-08-11', end: '2025-08-16' },
  { start: '2025-08-20', end: '2025-08-24' },
  { start: '2025-08-28', end: '2025-08-31' },
  { start: '2025-09-05', end: '2025-09-08' },
  { start: '2025-09-15', end: '2025-09-19' },
];

function dateToStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function strToDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isDateBooked(dateStr: string): boolean {
  const d = strToDate(dateStr);
  return BOOKED_RANGES.some(({ start, end }) => {
    const s = strToDate(start);
    const e = strToDate(end);
    return d >= s && d <= e;
  });
}

function hasBookedDatesInRange(startStr: string, endStr: string): boolean {
  const start = strToDate(startStr);
  const end = strToDate(endStr);
  const cur = new Date(start);
  while (cur <= end) {
    if (isDateBooked(dateToStr(cur))) return true;
    cur.setDate(cur.getDate() + 1);
  }
  return false;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

interface CalendarMonthProps {
  year: number;
  month: number;
  checkIn: string | null;
  checkOut: string | null;
  hovered: string | null;
  onDayClick: (dateStr: string) => void;
  onDayHover: (dateStr: string | null) => void;
}

function CalendarMonth({ year, month, checkIn, checkOut, hovered, onDayClick, onDayHover }: CalendarMonthProps) {
  const today = new Date();
  const todayStr = dateToStr(today);
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const cells: (number | null)[] = [];

  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const rangeEnd = checkIn && !checkOut ? hovered : checkOut;

  return (
    <div className="flex-1 min-w-[260px]">
      <h4 className="text-center text-sm font-semibold text-gray-800 mb-3">
        {MONTH_NAMES[month]} {year}
      </h4>
      <div className="grid grid-cols-7 mb-1">
        {DAY_NAMES.map((d) => (
          <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, idx) => {
          if (!day) return <div key={`e-${idx}`} />;

          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isPast = dateStr < todayStr;
          const isBooked = isDateBooked(dateStr);
          const isCheckIn = dateStr === checkIn;
          const isCheckOut = dateStr === checkOut;

          let isInRange = false;
          if (checkIn && rangeEnd) {
            const [a, b] = checkIn <= rangeEnd ? [checkIn, rangeEnd] : [rangeEnd, checkIn];
            isInRange = dateStr > a && dateStr < b;
          }

          const isDisabled = isPast || isBooked;

          let cellClass = 'relative h-9 flex items-center justify-center text-sm font-medium transition-all select-none ';

          if (isCheckIn || isCheckOut) {
            cellClass += 'bg-teal-600 text-white rounded-full z-10 cursor-pointer ';
          } else if (isInRange) {
            cellClass += 'bg-teal-100 text-teal-800 cursor-pointer ';
          } else if (isDisabled) {
            cellClass += 'text-gray-300 cursor-not-allowed ';
            if (isBooked && !isPast) cellClass += 'line-through ';
          } else {
            cellClass += 'text-gray-700 hover:bg-teal-50 hover:text-teal-700 rounded-full cursor-pointer ';
          }

          return (
            <div
              key={dateStr}
              className={cellClass}
              title={isBooked ? 'Ocupado' : isPast ? '' : 'Disponível'}
              onClick={() => !isDisabled && onDayClick(dateStr)}
              onMouseEnter={() => !isDisabled && onDayHover(dateStr)}
              onMouseLeave={() => onDayHover(null)}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface DateRangePickerProps {
  checkIn: string | null;
  checkOut: string | null;
  onChange: (checkIn: string | null, checkOut: string | null) => void;
}

export default function DateRangePicker({ checkIn, checkOut, onChange }: DateRangePickerProps) {
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const [baseMonth, setBaseMonth] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const secondMonth = baseMonth.month === 11
    ? { year: baseMonth.year + 1, month: 0 }
    : { year: baseMonth.year, month: baseMonth.month + 1 };

  const canGoPrev =
    baseMonth.year > today.getFullYear() ||
    (baseMonth.year === today.getFullYear() && baseMonth.month > today.getMonth());

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleDayClick(dateStr: string) {
    setError(null);
    if (!checkIn || (checkIn && checkOut)) {
      onChange(dateStr, null);
    } else {
      const [start, end] = checkIn <= dateStr ? [checkIn, dateStr] : [dateStr, checkIn];
      if (hasBookedDatesInRange(start, end)) {
        setError('O período selecionado inclui datas ocupadas. Por favor escolha outro intervalo.');
        onChange(start, null);
        return;
      }
      onChange(start, end);
      setOpen(false);
    }
  }

  function formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    const d = strToDate(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  function calcNights(): number {
    if (!checkIn || !checkOut) return 0;
    const diff = strToDate(checkOut).getTime() - strToDate(checkIn).getTime();
    return Math.round(diff / (1000 * 60 * 60 * 24));
  }

  const nights = calcNights();

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label className="text-sm font-medium text-gray-700">Datas de Estadia</label>

      {/* Trigger */}
      <div
        className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2.5 cursor-pointer hover:border-teal-400 transition-colors shadow-sm min-w-[320px]"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="w-5 h-5 flex items-center justify-center text-teal-600">
          <i className="ri-calendar-line text-base"></i>
        </div>
        <div className="flex items-center gap-2 flex-1 text-sm">
          <span className={checkIn ? 'text-gray-800 font-medium' : 'text-gray-400'}>
            {checkIn ? formatDate(checkIn) : 'Check-in'}
          </span>
          <i className="ri-arrow-right-line text-gray-400 text-xs"></i>
          <span className={checkOut ? 'text-gray-800 font-medium' : 'text-gray-400'}>
            {checkOut ? formatDate(checkOut) : 'Check-out'}
          </span>
          {nights > 0 && (
            <span className="ml-auto text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full font-medium">
              {nights} {nights === 1 ? 'noite' : 'noites'}
            </span>
          )}
        </div>
        {(checkIn || checkOut) && (
          <button
            className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); onChange(null, null); setError(null); }}
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        )}
      </div>

      {/* Dropdown Calendar */}
      {open && (
        <div className="absolute z-50 mt-1 bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 w-auto"
          style={{ top: 'calc(100% + 4px)', left: 0 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => canGoPrev && setBaseMonth((p) => p.month === 0 ? { year: p.year - 1, month: 11 } : { year: p.year, month: p.month - 1 })}
              disabled={!canGoPrev}
              className={`w-8 h-8 flex items-center justify-center rounded-full border transition-all cursor-pointer ${canGoPrev ? 'border-gray-300 text-gray-700 hover:bg-gray-100' : 'border-gray-100 text-gray-300 cursor-not-allowed'}`}
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <span className="text-xs text-gray-500 font-medium">
              {!checkIn ? 'Selecione o check-in' : !checkOut ? 'Selecione o check-out' : `${nights} ${nights === 1 ? 'noite' : 'noites'} selecionadas`}
            </span>
            <button
              onClick={() => setBaseMonth((p) => p.month === 11 ? { year: p.year + 1, month: 0 } : { year: p.year, month: p.month + 1 })}
              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>

          {/* Two months */}
          <div className="flex gap-8">
            <CalendarMonth
              year={baseMonth.year}
              month={baseMonth.month}
              checkIn={checkIn}
              checkOut={checkOut}
              hovered={hovered}
              onDayClick={handleDayClick}
              onDayHover={setHovered}
            />
            <div className="w-px bg-gray-100 self-stretch"></div>
            <CalendarMonth
              year={secondMonth.year}
              month={secondMonth.month}
              checkIn={checkIn}
              checkOut={checkOut}
              hovered={hovered}
              onDayClick={handleDayClick}
              onDayHover={setHovered}
            />
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-teal-600"></div>
              <span className="text-xs text-gray-500">Selecionado</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-teal-100"></div>
              <span className="text-xs text-gray-500">Período</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-gray-100"></div>
              <span className="text-xs text-gray-500">Ocupado</span>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-3 flex items-center gap-2 bg-rose-50 text-rose-600 text-xs px-3 py-2 rounded-lg">
              <i className="ri-error-warning-line"></i>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
